import * as React from 'react'
import { Check, Copy } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'

const CONFIRM_MS = 1200

/**
 * Copy `value`, reporting whether it actually landed.
 *
 * Exported because one caller copies without a click: the verification-code
 * panel puts the code on the clipboard the moment it arrives, since that is
 * where it was going next anyway. The boolean is the point — outside a secure
 * context `navigator.clipboard` is missing entirely and the write silently
 * fails, so a caller must never claim "copied" without checking.
 */
export async function writeClipboard(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // Permission denied or a non-secure context — fall through.
  }

  // Fallback for http:// origins, where the async clipboard API is unavailable.
  try {
    const el = document.createElement('textarea')
    el.value = value
    el.setAttribute('readonly', '')
    el.style.position = 'fixed'
    el.style.opacity = '0'
    document.body.appendChild(el)
    el.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(el)
    return ok
  } catch {
    return false
  }
}

/**
 * Copies `value`, then shows a tick for 1.2s. Used all over the ID fields.
 *
 * 24px is a comfortable inline target with a mouse and an impossible one with a
 * thumb, so the control grows to 36px below `md` — where it is also the only
 * way to get an id off the screen, since there is no right-click menu.
 */
export function CopyButton(p: { value: string; label?: string; className?: string }) {
  const [copied, setCopied] = React.useState(false)
  const timer = React.useRef<number | null>(null)

  React.useEffect(() => {
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    }
  }, [])

  const onClick = async () => {
    const ok = await writeClipboard(p.value)
    if (!ok) return
    setCopied(true)
    if (timer.current !== null) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => {
      setCopied(false)
      timer.current = null
    }, CONFIRM_MS)
  }

  const label = p.label ?? 'Copy'

  return (
    <>
      <Tooltip content={copied ? 'Copied' : label}>
        <Button
          variant="ghost"
          size="xs"
          iconOnly
          aria-label={copied ? `Copied ${p.value}` : `${label} ${p.value}`}
          onClick={() => void onClick()}
          className={cn('max-md:size-9 max-md:[&_svg]:size-4', copied && 'text-ok', p.className)}
        >
          {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
        </Button>
      </Tooltip>
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </>
  )
}
